import { Plus, X } from "@untitledui/icons";
import { useMemo, useRef, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Heading } from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { ReferenceFormatHelp } from "./reference-format-help";
import { compileReferenceFormatMask, maskPresets } from "./reference-format-mask";
import {
	MAX_FORMATS,
	type ReferenceRequirementSchema,
	referenceRequirementSchema,
} from "./reference-requirement-schema";

interface MaskRow {
	id: number;
	value: string;
}

interface RequirementDraft {
	label: string;
	masks: MaskRow[];
	hint: string;
	example: string;
}

interface DraftErrors {
	label?: string;
	hint?: string;
	example?: string;
	formats?: string;
	masks: Record<number, string>;
}

const toDraft = (requirement: ReferenceRequirementSchema | undefined): RequirementDraft => ({
	label: requirement?.label ?? "",
	masks: (requirement?.masks ?? []).map((mask, index) => ({ id: index, value: mask.value })),
	hint: requirement?.hint ?? "",
	example: requirement?.example ?? "",
});

const validateDraft = (draft: RequirementDraft, otherLabels: string[]): DraftErrors => {
	const errors: DraftErrors = { masks: {} };
	const result = referenceRequirementSchema.safeParse({
		label: draft.label,
		masks: draft.masks.map((mask) => ({ value: mask.value })),
		hint: draft.hint,
		example: draft.example,
	});

	if (!result.success) {
		for (const issue of result.error.issues) {
			const [field, index] = issue.path;
			if (field === "label" && !errors.label) {
				errors.label = issue.message;
			} else if (field === "hint" && !errors.hint) {
				errors.hint = issue.message;
			} else if (field === "example" && !errors.example) {
				errors.example = issue.message;
			} else if (field === "masks" && typeof index === "number") {
				errors.masks[index] ??= issue.message;
			} else if (field === "masks" && !errors.formats) {
				errors.formats = issue.message;
			}
		}
	}

	const label = draft.label.trim().toLowerCase();
	if (!errors.label && otherLabels.some((existing) => existing.trim().toLowerCase() === label)) {
		errors.label = "Labels must be unique";
	}

	return errors;
};

const hasErrors = (errors: DraftErrors): boolean =>
	Boolean(errors.label || errors.hint || errors.example || errors.formats) || Object.keys(errors.masks).length > 0;

export interface ReferenceRequirementModalProps {
	requirement?: ReferenceRequirementSchema;
	otherLabels: string[];
	onSave: (requirement: ReferenceRequirementSchema) => void;
	onClose: () => void;
}

export const ReferenceRequirementModal = ({
	requirement,
	otherLabels,
	onSave,
	onClose,
}: ReferenceRequirementModalProps) => {
	const [draft, setDraft] = useState(() => toDraft(requirement));
	const [isSubmitted, setIsSubmitted] = useState(false);
	const nextMaskId = useRef(draft.masks.length);

	const errors = useMemo(() => validateDraft(draft, otherLabels), [draft, otherLabels]);
	const sample = draft.example.trim();
	const isFull = draft.masks.length >= MAX_FORMATS;

	const addMask = (value: string) => {
		nextMaskId.current += 1;
		setDraft((current) => ({ ...current, masks: [...current.masks, { id: nextMaskId.current, value }] }));
	};

	// Adding a format that is already present is always a mis-click, so ignore it rather than flag it later
	const addPresetMask = (value: string) => {
		const alreadyAdded = draft.masks.some((mask) => mask.value.trim().toLowerCase() === value.trim().toLowerCase());
		if (!alreadyAdded) {
			addMask(value);
		}
	};

	const updateMask = (id: number, value: string) => {
		setDraft((current) => ({
			...current,
			masks: current.masks.map((mask) => (mask.id === id ? { ...mask, value } : mask)),
		}));
	};

	const removeMask = (id: number) => {
		setDraft((current) => ({ ...current, masks: current.masks.filter((mask) => mask.id !== id) }));
	};

	const matchIndicator = (value: string) => {
		if (!sample) {
			return null;
		}
		const compiled = compileReferenceFormatMask(value.trim());
		if (!compiled) {
			return null;
		}
		return compiled.test(sample) ? (
			<span className="text-sm font-medium text-success-primary">Matches</span>
		) : (
			<span className="text-sm font-medium text-error-primary">No match</span>
		);
	};

	const save = () => {
		setIsSubmitted(true);
		if (hasErrors(errors)) {
			return;
		}
		onSave({
			label: draft.label.trim(),
			masks: draft.masks.map((mask) => ({ value: mask.value.trim() })),
			hint: draft.hint.trim(),
			example: draft.example.trim(),
		});
	};

	return (
		<ModalOverlay isOpen onOpenChange={(open) => !open && onClose()} className="items-center">
			<Modal className="max-w-2xl">
				<Dialog>
					<div className="w-full rounded-lg bg-white">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<Heading slot="title" level={3} className="text-lg font-semibold text-gray-900">
								{requirement ? "Edit reference requirement" : "Add reference requirement"}
							</Heading>
							<Button color="tertiary" size="sm" onClick={onClose}>
								<X className="size-5" />
							</Button>
						</div>
						<div className="flex max-h-[70vh] flex-col gap-5 overflow-y-auto p-4">
							<Input
								label="Label"
								isRequired
								autoFocus
								value={draft.label}
								onChange={(value) => setDraft((current) => ({ ...current, label: value }))}
								placeholder="e.g. Invoice ID"
								isInvalid={isSubmitted && Boolean(errors.label)}
								hint={isSubmitted ? errors.label : undefined}
							/>
							<div className="flex flex-col gap-3">
								<div className="flex items-center gap-1.5">
									<span className="text-sm font-medium text-secondary">Formats</span>
									<ReferenceFormatHelp />
								</div>
								{draft.masks.length === 0 && (
									<p className="text-sm text-tertiary">Any value is accepted unless you add a format.</p>
								)}
								<div className="flex flex-wrap items-center gap-2">
									<Select
										aria-label="Add a pre-configured format"
										placeholder="Add a pre-configured format"
										items={maskPresets}
										selectedKey={null}
										isDisabled={isFull}
										onSelectionChange={(key) => {
											const preset = maskPresets.find((candidate) => candidate.id === key);
											if (preset) {
												addPresetMask(preset.mask);
											}
										}}
										className="w-64"
									>
										{(item) => <SelectItem {...item}>{item.label}</SelectItem>}
									</Select>
									<Button
										color="secondary"
										size="sm"
										iconLeading={Plus}
										isDisabled={isFull}
										onClick={() => addMask("")}
									>
										Add custom format
									</Button>
								</div>
								{isSubmitted && errors.formats && <p className="text-sm text-error-primary">{errors.formats}</p>}
								{draft.masks.length > 0 && (
									<div className="flex flex-col gap-2">
										{draft.masks.map((mask, index) => (
											<div key={mask.id} className="flex items-start gap-3">
												<Input
													aria-label={`Format ${index + 1}`}
													value={mask.value}
													onChange={(value) => updateMask(mask.id, value)}
													placeholder="e.g. INV-[#6]"
													inputClassName="font-mono"
													isInvalid={isSubmitted && Boolean(errors.masks[index])}
													hint={isSubmitted ? errors.masks[index] : undefined}
													className="flex-1"
												/>
												<div className="flex shrink-0 items-center gap-3 pt-2">
													{matchIndicator(mask.value)}
													<Button color="link-destructive" size="sm" onClick={() => removeMask(mask.id)}>
														Remove
													</Button>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
							<Input
								label="Example value (optional)"
								value={draft.example}
								onChange={(value) => setDraft((current) => ({ ...current, example: value }))}
								placeholder="e.g. INV-004312"
								isInvalid={isSubmitted && Boolean(errors.example)}
								hint={
									isSubmitted && errors.example
										? errors.example
										: "Shown to the talent member so they know what a valid value looks like."
								}
							/>
							<TextArea
								label="Hint (optional)"
								value={draft.hint}
								onChange={(value) => setDraft((current) => ({ ...current, hint: value }))}
								placeholder="e.g. Found in the top-right corner of the invoice"
								rows={3}
								isInvalid={isSubmitted && Boolean(errors.hint)}
								hint={
									isSubmitted && errors.hint
										? errors.hint
										: "Shown to the talent member when their value doesn't match."
								}
							/>
						</div>
						<div className="flex gap-3 border-t border-gray-200 px-4 py-3">
							<Button color="secondary" className="flex-1" onClick={onClose}>
								Cancel
							</Button>
							<Button color="primary" className="flex-1" onClick={save}>
								Save
							</Button>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
};
